import { CSSProperties, useEffect, useRef, useState } from "react";
// import Image from "next/image";
import { useComponentState } from "@faedev/dynamic-values";
import { composeClassNames, withDefault } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { Alert, Button, Col, Row } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import Hr from "src/components/Hr";
import IntroductionActions from "src/components/IntroductionActions";
import IntroductionInfo from "src/components/IntroductionInfo";
import Price from "src/components/Price";
import PriceVariants from "src/components/PriceVariants";
import PublishButtons from "src/components/PublishButtons";
import ReadMore from "src/components/ReadMore";
import RichText from "src/components/RichText";
import SellButtons from "src/components/SellButtons";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import getLink from "src/functions/getLink";
import gutter from "src/functions/gutter";
import profileNameText from "src/functions/profileNameText";
import translate from "src/functions/translate";
import useLoggedInUser from "src/functions/useLoggedInUser";
import validateAuthToken from "src/functions/validateAuthToken";
import Check from "src/icons/Check";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sIntroduction } from "src/styles/sIntroduction.module.scss";
import { sSerieIntroduction } from "src/styles/sSerieIntroduction.module.scss";
import { ApiPrograms } from "src/types/ApiPrograms";
import ModalSubscription from "./ModalSubscription";
import Preloader from "./Preloader";
import Video from "./Video";

type Props = {
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
  data: ApiPrograms;
  freshDataLoaded: boolean;
};

function SerieIntroduction({ style, className, data, freshDataLoaded }: Props) {
  const { locale: lang } = useRouter();
  const [newPrice, setNewPrice] = useState(data?.price);
  const [priceVariants, setPriceVariants] = useState(null);
  const [loggedUserClientId, setLoggedUserClientId] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const titleRef = useRef(null);
  var openModalSubscription = useComponentState(false);

  useEffect(() => {
    try {
      const pv = require(`src/customs/serie_${data?.id}_variants.json`);
      setPriceVariants(pv);
    } catch (err) {}
  }, [data.id]);

  const profileDefined =
    data.profiles && Object.keys(data.profiles).length > 0 ? true : false;

  const videoDefined =
    data.video &&
    Object.keys(data.video).length > 0 &&
    data.video.video_id?.length > 0
      ? true
      : false;

  const videoMuxPaid =
    loggedUserClientId !== null &&
    constants.Show.contentMux &&
    data.video &&
    Object.keys(data.video).length > 0 &&
    data.video.mux &&
    Object.keys(data.video.mux).length > 0 &&
    data.video.mux.playback_id?.length > 0 &&
    data.isPaid
      ? true
      : false;

  const serieExpired = data && data.end_at && new Date(data.end_at);
  const actualDate = new Date();
  const isSerieExpired = serieExpired ? actualDate > serieExpired : false;

  var userResult = useLoggedInUser();
  const [activeTrialSubscription, setActiveTrialSubscription] = useState(false);

  useEffect(() => {
    userResult.result &&
      fetch(`${constants.Paths.apiUrl}/clients/me`, {
        method: "GET",
        headers: validateAuthToken(getCookieByName(`auth-token`)),
      }).then(async (res) => {
        if (res.ok) {
          const result = await res.json();
          const trialSubscriptionTill = result.subs_trial_till;
          const today = new Date();
          const isTrialAllowed = data.subscription_allowed === "no_trial";
          const clientId = result.id;
          setLoggedUserClientId(clientId);

          if (new Date(trialSubscriptionTill) >= today && isTrialAllowed) {
            setActiveTrialSubscription(true);
          }
        }
      });
  }, [userResult.result, data]);

  return data ? (
    <Row
      style={style}
      className={composeClassNames([
        sIntroduction,
        sSerieIntroduction,
        className,
        "serie-introduction",
        videoMuxPaid && "full-width",
      ])}
      ref={titleRef}
    >
      <Col lg={7} className={"serie-preview"}>
        <Video
          data={data}
          videoMuxPaid={videoMuxPaid}
          videoDefined={videoDefined}
          clientId={loggedUserClientId}
        />
        <PublishButtons className={`serie-buttons`} />
      </Col>
      <Col lg={5} className={"serie-detail"}>
        <Heading type="h1" title={data.title} />
        {profileDefined && (
          <IntroductionInfo
            type={`serie`}
            data={[
              {
                item: profileNameText(
                  data.profiles[0].client?.name,
                  data.profiles[0].client?.surname,
                  data.profiles[0].nickname,
                  data.profiles[0].type
                ),
                url: getLink(
                  data.profiles[0].locale,
                  `profile`,
                  data.profiles[0].slug
                ),
              },
              { item: data.items },
              { item: String(Math.round(data.views / 100)) },
            ]}
          />
        )}
        <IntroductionActions
          data={{
            favorite: true,
            rating: withDefault(data.rating, 0),
            paid: data.paid,
            isPaid: data.isPaid,
            subscription: data?.subscription,
          }}
        />
        {freshDataLoaded ? (
          data.price && (
            <>
              {data.isPaid && (
                <Alert
                  variant="success"
                  className={composeClassNames([sAlert])}
                  style={gutter(`margin`, `top`, `lg`)}
                >
                  <Check /> <Translate tKey={`You have purchased the serie.`} />
                </Alert>
              )}
              {!data.isPaid && data?.subscription && !activeTrialSubscription && (
                <>
                  <Button
                    variant="primary"
                    size="lg"
                    className={composeClassNames([sButton])}
                    onClick={() => openModalSubscription.change(true)}
                  >
                    <Translate tKey="Play in subscription" />
                  </Button>
                  <Heading
                    type="h5"
                    title={<Translate tKey={`or buy separately`} />}
                    style={gutter(`margin`, `top`, `lg`)}
                    uppercase
                  />
                  <ModalSubscription
                    openModalSubscription={openModalSubscription}
                  />
                </>
              )}
              {!data.isPaid &&
                !isSerieExpired &&
                data.price &&
                (!priceVariants ? (
                  <Price
                    currency={`CZK`} // TODO: currency should be dynamic AND implement price basic
                    price={data.price}
                    priceBasic={data.price_basic}
                    size={
                      data?.subscription && !activeTrialSubscription
                        ? `md`
                        : `lg`
                    }
                    style={
                      data?.subscription && !activeTrialSubscription
                        ? { marginTop: 0 }
                        : {}
                    }
                  />
                ) : (
                  <PriceVariants
                    currency={
                      priceVariants?.currency ||
                      constants.Locales._default.currency
                    }
                    price={data.price}
                    priceAlternatives={priceVariants?.prices}
                    /* @ts-ignore */
                    setNewPrice={setNewPrice}
                    size="xm"
                    title={
                      priceVariants?.title &&
                      translate(priceVariants?.title, lang)
                    }
                  />
                ))}
              {!data.isPaid && constants.Show.sale && !isSerieExpired && (
                <>
                  <SellButtons
                    entityId={data.id}
                    className={`align-left`}
                    id={1}
                    inCart={data.inCart}
                    newPrice={newPrice ? newPrice : null}
                    orderId={""}
                    type={"program"} // TODO: there is "program", but should be "serie" instead
                    size={
                      data?.subscription && !activeTrialSubscription
                        ? "sm"
                        : "lg"
                    }
                    btnPrimary={
                      priceVariants?.btnPrimary || {
                        display: true,
                        variant:
                          data?.subscription && !activeTrialSubscription
                            ? "outline-primary"
                            : "primary",
                      }
                    }
                    btnSecondary={priceVariants?.btnSecondary || undefined}
                  />
                  {data.inCart && (
                    <Alert
                      variant={`info`}
                      className={composeClassNames([sAlert])}
                      style={gutter(`margin`, `top`, `md`)}
                    >
                      <Translate tKey={`Serie is already in the cart.`} />
                    </Alert>
                  )}
                </>
              )}
              {deadline && deadline}
            </>
          )
        ) : (
          <Text
            size="md"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <Preloader />
            <Translate tKey={`Loading prices`} />
          </Text>
        )}
        {isSerieExpired && (
          <Alert
            variant={`info`}
            className={composeClassNames([sAlert])}
            style={gutter(`margin`, `top`, `lg`)}
          >
            <Translate tKey={`It is no longer possible to buy the serie.`} />
          </Alert>
        )}
      </Col>
      {data.description?.text?.length > 0 && (
        <Col className={"serie-description"}>
          <Hr variant={`light`} />
          <ReadMore
            rowsVisible={constants.Text.descriptionRowsLong}
            rowsCount={
              data.description.text.length /
              constants.Text.descriptionCharsPerRow
            }
          >
            <RichText size="xn">
              <ReactMarkdown>{data.description.text}</ReactMarkdown>
            </RichText>
          </ReadMore>
        </Col>
      )}
    </Row>
  ) : null;
}

export default SerieIntroduction;
